/**
 * Utility functions for parsing and organizing equipment data
 */

export interface ParsedEquipmentName {
  projectCode: string;
  zone: string;
  asset: string;
  subLocation: string;
  sequence: string;
  full: string;
  childSequence?: string;
  grandChildSequence?: string;
  grandChildType?: string; // M or C or None
  wtgLabel?: string;
}

/**
 * Parse equipment name to extract zone, asset, sublocation, and sequence
 * Expected format: PROJECTCODE-ZONE-ASSET-SUBLOCATION-SEQUENCE
 * Examples:
 * - PRJ-Z02-ASS-T098-001
 * - KHND25-Z01-INV-R12-015
 * - PRTR-001-INV-001 (legacy format without zone)
 * - R8-T1-INV001 (Parent)
 * - R8-T1-INV001-S1 (Child)
 * - R8-T1-INV001-S1-M1 (Grandchild)
 */
export function parseEquipmentName(equipmentName: string): ParsedEquipmentName | null {
  if (!equipmentName) return null;

  // Split by dash
  const parts = equipmentName.split('-');

  // New Hierarchical format: PREFIX-N-PREFIX-N-PREFIX-N
  // Example: TRA-1-INV-1-STR-5
  if (parts.length >= 2) {
    // If it's a child, it will have at least 4 parts (ParentPrefix, ParentSeq, ChildPrefix, ChildSeq)
    // If it's a root, it has 2 parts (Prefix, Seq)
    
    return {
      projectCode: '',
      zone: '', // In the new format, zone might be part of the hierarchy labels
      subLocation: '',
      asset: parts[parts.length - 2], // Second to last is the asset prefix
      sequence: parts[parts.length - 1], // Last is the sequence
      full: equipmentName
    };
  }

  return null;
}

/**
 * Extract zone from equipment data or equipment name
 */
export function getEquipmentZone(equipment: any): string {
  // Check if zone is explicitly provided
  if (equipment.zone) return equipment.zone;
  if (equipment.block) return equipment.block;
  if (equipment.siteLabel) return equipment.siteLabel;
  if (equipment.wtgId) return equipment.wtgId;

  // Try to parse from equipment name
  const equipmentName = equipment.equipmentName || equipment.name || equipment.equipmentId;
  const parsed = parseEquipmentName(equipmentName);
  if (parsed && parsed.zone) {
    return parsed.zone;
  }

  return 'Unknown Zone';
}

/**
 * Extract sublocation from equipment data or equipment name
 */
export function getEquipmentSubLocation(equipment: any): string {
  if (equipment.subLocation) return equipment.subLocation;

  const equipmentName = equipment.equipmentName || equipment.name || equipment.equipmentId;
  const parsed = parseEquipmentName(equipmentName);
  if (parsed && parsed.subLocation) {
    return parsed.subLocation;
  }

  return 'No SubLocation';
}

/**
 * Extract asset type from equipment data or equipment name
 */
export function getEquipmentAsset(equipment: any): string {
  if (equipment.assetName) return equipment.assetName;

  const equipmentName = equipment.equipmentName || equipment.name || equipment.equipmentId;
  const parsed = parseEquipmentName(equipmentName);
  if (parsed && parsed.asset) {
    return parsed.asset;
  }

  return 'Unknown Asset';
}

/**
 * Get equipment base name (everything except sequence)
 */
export function getEquipmentBaseName(equipmentName: string): string {
  if (!equipmentName) return '';

  const parsed = parseEquipmentName(equipmentName);
  if (!parsed) return equipmentName;

  const parts = equipmentName.split('-');
  if (parts.length > 1) {
    return parts.slice(0, -1).join('-');
  }

  return equipmentName;
}

/**
 * Group equipment by zone -> sublocation -> asset -> equipment list
 */
export interface EquipmentHierarchy {
  zones: {
    [zoneName: string]: {
      zoneName: string;
      totalCount: number;
      subLocations: {
        [subLocationName: string]: {
          subLocationName: string;
          totalCount: number;
          assets: {
            [assetName: string]: {
              assetName: string;
              equipment: any[];
            };
          };
        };
      };
    };
  };
}

export function organizeEquipmentHierarchy(equipmentList: any[]): EquipmentHierarchy {
  const hierarchy: EquipmentHierarchy = { zones: {} };

  equipmentList.forEach((equipment) => {
    const zone = getEquipmentZone(equipment);
    const subLocation = getEquipmentSubLocation(equipment);
    const asset = getEquipmentAsset(equipment);

    if (!hierarchy.zones[zone]) {
      hierarchy.zones[zone] = {
        zoneName: zone,
        totalCount: 0,
        subLocations: {}
      };
    }

    if (!hierarchy.zones[zone].subLocations[subLocation]) {
      hierarchy.zones[zone].subLocations[subLocation] = {
        subLocationName: subLocation,
        totalCount: 0,
        assets: {}
      };
    }

    if (!hierarchy.zones[zone].subLocations[subLocation].assets[asset]) {
      hierarchy.zones[zone].subLocations[subLocation].assets[asset] = {
        assetName: asset,
        equipment: []
      };
    }

    hierarchy.zones[zone].subLocations[subLocation].assets[asset].equipment.push(equipment);
    hierarchy.zones[zone].totalCount++;
    hierarchy.zones[zone].subLocations[subLocation].totalCount++;
  });

  return hierarchy;
}

export function groupEquipmentByBaseName(equipmentList: any[]): Array<{
  baseName: string;
  displayName: string;
  items: any[];
  isGroup: boolean;
}> {
  const groups: Record<string, any[]> = {};

  equipmentList.forEach((equipment) => {
    const equipmentName = equipment.equipmentName || equipment.name || equipment.equipmentId;
    const baseName = getEquipmentBaseName(equipmentName);

    if (!groups[baseName]) {
      groups[baseName] = [];
    }
    groups[baseName].push(equipment);
  });

  return Object.entries(groups).map(([baseName, items]) => {
    const isGroup = items.length > 1;
    let displayName = baseName;

    if (isGroup) {
      const sequences = items
        .map((item) => {
          const name = item.equipmentName || item.name || item.equipmentId;
          const parsed = parseEquipmentName(name);
          return parsed ? parsed.sequence : null;
        })
        .filter(Boolean)
        .sort((a, b) => parseInt(a!) - parseInt(b!));

      if (sequences.length > 0) {
        displayName = `${baseName} (${sequences[0]}-${sequences[sequences.length - 1]})`;
      }
    } else {
      displayName = items[0].equipmentName || items[0].name || items[0].equipmentId;
    }

    return {
      baseName,
      displayName,
      items,
      isGroup
    };
  });
}

// === Solar Hierarchy Tree Builder ===

export interface SolarNode {
  id: string;
  name: string;
  type: 'Grid' | 'HTYard' | 'Transformer' | 'ACDB' | 'ACCable' | 'Inverter' | 'DCCable' | 'MMS' | 'Table' | 'String' | 'Module' | 'Other';
  children: SolarNode[];
  equipment: any;
}

/**
 * Build a hierarchical tree for Solar Equipment
 * Follows strict structure: GRID -> HT_YARD -> TRANSFORMER -> ACDB -> AC_CABLE -> INVERTER -> DC_CABLE -> MMS -> TABLE -> STRING -> MODULE
 */
export function buildSolarHierarchyTree(equipmentList: any[]): SolarNode[] {
  const nodeMap: Record<string, SolarNode> = {};
  const roots: SolarNode[] = [];

  // 1. Create nodes
  equipmentList.forEach(eq => {
    const name = eq.equipmentName || eq.equipmentId;
    if (!name) return;

    let type: SolarNode['type'] = 'Other';
    const upperName = name.toUpperCase();
    const parts = name.split('-');
    const lastPart = parts[parts.length - 1] || '';

    // Hierarchy Detection logic
    if (upperName.includes('GRID')) type = 'Grid';
    else if (upperName.includes('HTY') || upperName.includes('HT_YARD')) type = 'HTYard';
    else if (upperName.includes('TRF') || upperName.includes('TRANSFORMER') || /^T\d+$/.test(lastPart)) type = 'Transformer';
    else if (upperName.includes('ACDB')) type = 'ACDB';
    else if (upperName.includes('AC_CABLE') || upperName.includes('ACC')) type = 'ACCable';
    else if (upperName.includes('INV') || upperName.includes('INVERTER')) type = 'Inverter';
    else if (upperName.includes('DC_CABLE') || upperName.includes('DCC')) type = 'DCCable';
    else if (upperName.includes('MMS')) type = 'MMS';
    else if (upperName.includes('TABLE') || upperName.includes('TBL')) type = 'Table';
    else if (upperName.includes('STRING') || /^S\d+$/.test(lastPart)) type = 'String';
    else if (upperName.includes('MODULE') || /^M\d+$/.test(lastPart)) type = 'Module';
    else if (upperName.includes('CABLE') || /^C\d+$/.test(lastPart)) type = 'DCCable'; // Default to DC Cable if just Cable
    else if (/^CB\d+$/.test(lastPart)) type = 'Other'; // Combiner -> Other/Generic
    else if (/^SG\d+$/.test(lastPart)) type = 'Other'; // Switchgear -> Other/Generic
    else if (/^MTB\d+$/.test(lastPart)) type = 'Other'; // Termination -> Other/Generic

    nodeMap[name] = {
      id: name,
      name: name,
      type,
      children: [],
      equipment: eq
    };
  });

  // 2. Link children to parents (prefer parentId — works with short GP_LEAF-PARENT_LEAF child names)
  Object.values(nodeMap).forEach(node => {
    const eq = node.equipment;
    const pid = eq?.parentId ?? eq?.parentEquipmentId;
    if (pid) {
      const parentEq = equipmentList.find(e => String(e._id || e.id) === String(pid));
      const parentKey = parentEq
        ? String(parentEq.equipmentName || parentEq.equipmentId || '')
        : '';
      if (parentKey && nodeMap[parentKey]) {
        nodeMap[parentKey].children.push(node);
        return;
      }
    }
    const parts = node.name.split('-');

    // Legacy: full-chain names (e.g. TRA-1-INV-1-STR-1): parent is before the last two segments
    if (parts.length >= 4) {
      const parentName = parts.slice(0, parts.length - 2).join('-');
      if (parentName && nodeMap[parentName]) {
        nodeMap[parentName].children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // 3. Sort children
  const sortNodes = (nodes: SolarNode[]) => {
    nodes.sort((a, b) => {
      const getLastNum = (s: string) => {
        const lastPart = s.split('-').pop() || '';
        const num = parseInt(lastPart.replace(/\D/g, '') || '0');
        return num;
      };
      return getLastNum(a.name) - getLastNum(b.name);
    });
    nodes.forEach(n => sortNodes(n.children));
  };

  sortNodes(roots);

  return roots.sort((a, b) => a.name.localeCompare(b.name));
}

/** Last two hyphen segments (CODE-N) — leaf level; matches backend equipment.model naming. */
export function getLeafEquipmentSegment(equipmentName: string): string {
  const parts = equipmentName.split('-').filter((p) => p.length > 0);
  if (parts.length <= 2) {
    return equipmentName.trim();
  }
  return parts.slice(-2).join('-');
}

/**
 * Generate next available Equipment ID
 * Naming convention:
 *   Root: ASSET_CODE-N  (e.g. TRA-1, INV-1)
 *   Child: ASSET_CODE-N only (sequence among siblings under the same parent; no parent name prefix)
 */
export function generateNextEquipmentId(
  existingEquipment: any[],
  zone: string,
  subLocation: string,
  assetCode: string,
  parentId?: string,
  type: SolarNode['type'] = 'Inverter'
): string {
  const code = assetCode || 'EQP';

  if (parentId) {
    const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${escapedCode}-(\\d+)$`);
    let maxSeq = 0;

    existingEquipment.forEach(eq => {
      if (String(eq.parentId ?? eq.parentEquipmentId ?? '') !== String(parentId)) {
        return;
      }
      const name = eq.equipmentName || eq.equipmentId;
      if (name) {
        const match = name.match(regex);
        if (match) {
          const seq = parseInt(match[1], 10);
          if (seq > maxSeq) maxSeq = seq;
        }
      }
    });

    return `${code}-${maxSeq + 1}`;
  }

  // Root: ASSET_CODE-N
  const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rootRegex = new RegExp(`^${escapedCode}-(\\d+)$`);
  let maxSeq = 0;

  existingEquipment.forEach(eq => {
    const name = eq.equipmentName || eq.equipmentId;
    if (name) {
      const match = name.match(rootRegex);
      if (match) {
        const seq = parseInt(match[1], 10);
        if (seq > maxSeq) maxSeq = seq;
      }
    }
  });

  return `${code}-${maxSeq + 1}`;
}
